import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import { Button, cx } from "./components";

/** Ring for every text control (inputs, selects, textareas): 3:1 against its surface, darker and thicker on focus. */
export function controlRing(error?: boolean) {
  return cx("outline-none ring-1 focus:ring-2 focus-within:ring-2", error ? "ring-danger-text" : "ring-border-control focus:ring-text-primary focus-within:ring-text-primary");
}

/** Shared look for text inputs: cream field with a visible ring that darkens on focus. Read-only fields look inert. */
export function inputClass(error?: boolean) {
  return cx(
    "min-h-12 w-full rounded-control bg-surface-background px-4 text-body text-text-primary placeholder:text-text-muted read-only:bg-surface-subtle read-only:text-text-secondary read-only:ring-0",
    controlRing(error),
  );
}

/** Labelled text input with its error tied to it for screen readers. */
export function Field({ label, error, hint, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string | null; hint?: string }) {
  const id = useId();
  const describedBy = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("flex flex-col gap-1.5 text-left", className)}>
      <label htmlFor={id} className="text-small font-semibold text-text-primary">
        {label}
      </label>
      <input id={id} aria-invalid={error ? true : undefined} aria-describedby={describedBy} className={inputClass(!!error)} {...props} />
      {hint && !error && (
        <p id={`${id}-hint`} className="text-caption text-text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-caption font-semibold text-danger-text">
          {error}
        </p>
      )}
    </div>
  );
}

export function ErrorMessage({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-control bg-danger-soft px-4 py-3 text-small font-semibold text-danger-text">
      <span className="flex-1">{message}</span>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Notice({ children, tone = "info" }: { children: ReactNode; tone?: "info" | "success" | "warning" }) {
  const tones = { info: "bg-primary-soft text-text-primary", success: "bg-success-soft text-success-text", warning: "bg-warning-soft text-warning-text" };
  return <div className={cx("rounded-control px-4 py-3 text-small font-medium", tones[tone])}>{children}</div>;
}

/** Busy placeholder blocks; `label` is what screen readers hear while it loads. */
export function Skeleton({ className, label }: { className?: string; label?: string }) {
  return <div role={label ? "status" : undefined} aria-label={label} className={cx("animate-pulse rounded-card bg-surface-subtle", className)} />;
}

export function FullPageLoading({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" aria-label={label} className="grid min-h-dvh place-items-center">
      <span aria-hidden="true" className="size-8 animate-spin rounded-full border-4 border-text-primary border-r-transparent" />
    </div>
  );
}

/** Small uppercase badge (CURRENT PLAN, Read-only…). */
export function Badge({ children, tone = "soft" }: { children: ReactNode; tone?: "soft" | "accent" }) {
  return (
    <span className={cx("inline-flex self-start rounded-pill px-3 py-1 text-caption font-bold uppercase tracking-wider", tone === "accent" ? "bg-primary text-text-on-primary" : "bg-primary-soft text-text-primary")}>
      {children}
    </span>
  );
}
